import { useState, useMemo } from 'react';
import { ChevronRight, ChevronDown, ChevronsUpDown, Asterisk, Repeat } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import type { AdfElementNode } from '@/hooks/useAdfStandard';

interface AdfSidebarProps {
  elementTree: AdfElementNode[];
  activeSlug: string;
  onElementSelect: (slug: string) => void;
  isMobile?: boolean;
}

interface TreeNodeProps {
  node: AdfElementNode;
  level: number;
  activeSlug: string;
  onSelect: (slug: string) => void;
  expandedSlugs: Set<string>;
  toggleExpanded: (slug: string) => void;
}

function TreeNode({ node, level, activeSlug, onSelect, expandedSlugs, toggleExpanded }: TreeNodeProps) {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expandedSlugs.has(node.slug);
  const isActive = activeSlug === node.slug;

  return (
    <div>
      <div
        className={cn(
          'flex items-center gap-1 py-1.5 px-2 rounded-md cursor-pointer transition-colors text-sm',
          'hover:bg-muted/80',
          isActive && 'bg-primary/10 text-primary font-medium',
          level > 0 && 'ml-3'
        )}
        style={{ paddingLeft: `${8 + level * 12}px` }}
      >
        {hasChildren ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleExpanded(node.slug);
            }}
            className="p-0.5 hover:bg-muted rounded shrink-0"
          >
            {isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
            )}
          </button>
        ) : (
          <span className="w-4.5 shrink-0" />
        )}
        
        <button
          onClick={() => onSelect(node.slug)}
          className="flex items-center gap-2 flex-1 min-w-0 text-left"
        >
          <code className="font-mono text-xs truncate">
            &lt;{node.tag_name}&gt;
          </code>
          <span className="truncate text-muted-foreground text-xs hidden sm:inline">
            {node.display_name !== node.tag_name && node.display_name}
          </span>
        </button>
        
        <div className="flex items-center gap-1 shrink-0">
          {node.is_required && (
            <Asterisk className="h-3 w-3 text-green-600" aria-label="Required" />
          )}
          {node.is_repeatable && (
            <Repeat className="h-3 w-3 text-blue-600" aria-label="Repeatable" />
          )}
        </div>
      </div>

      {hasChildren && isExpanded && (
        <div>
          {node.children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              level={level + 1}
              activeSlug={activeSlug}
              onSelect={onSelect}
              expandedSlugs={expandedSlugs}
              toggleExpanded={toggleExpanded}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function AdfSidebar({ elementTree, activeSlug, onElementSelect, isMobile = false }: AdfSidebarProps) {
  // Initialize with root elements expanded
  const [expandedSlugs, setExpandedSlugs] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    elementTree.forEach((node) => initial.add(node.slug));
    return initial;
  });

  // Get all slugs for expand/collapse all
  const allSlugs = useMemo(() => {
    const slugs: string[] = [];
    const collect = (nodes: AdfElementNode[]) => {
      nodes.forEach((n) => {
        if (n.children?.length > 0) {
          slugs.push(n.slug);
          collect(n.children);
        }
      });
    };
    collect(elementTree);
    return slugs;
  }, [elementTree]);

  const toggleExpanded = (slug: string) => {
    setExpandedSlugs((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) {
        next.delete(slug);
      } else {
        next.add(slug);
      }
      return next;
    });
  };

  const expandAll = () => {
    setExpandedSlugs(new Set(allSlugs));
  };

  const collapseAll = () => {
    setExpandedSlugs(new Set());
  };

  return (
    <div className={cn('w-64 shrink-0', isMobile && 'w-full')}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-muted-foreground">Element Tree</h2>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={expandAll}
            className="h-7 px-2 text-xs"
          >
            Expand
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={collapseAll}
            className="h-7 px-2 text-xs"
          >
            Collapse
          </Button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 mb-3 text-xs text-muted-foreground">
        <div className="flex items-center gap-1">
          <Asterisk className="h-3 w-3 text-green-600" />
          <span>Required</span>
        </div>
        <div className="flex items-center gap-1">
          <Repeat className="h-3 w-3 text-blue-600" />
          <span>Repeatable</span>
        </div>
      </div>

      {/* Tree */}
      <ScrollArea className={cn('pr-2', isMobile ? 'h-[calc(100vh-200px)]' : 'h-[calc(100vh-280px)]')}>
        <div className="space-y-0.5">
          {elementTree.map((node) => (
            <TreeNode
              key={node.id}
              node={node}
              level={0}
              activeSlug={activeSlug}
              onSelect={onElementSelect}
              expandedSlugs={expandedSlugs}
              toggleExpanded={toggleExpanded}
            />
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}
